// === Admin: Fertilizer Companies ===
// Full CRUD page — list, add, edit, soft-delete.
// Ported from Click2Gro PHP (FertilzerCompaniesController.php / fertilzer_companies table)
// Columns displayed: company, street, suburb, postcode, state, phone_no + Actions
// Form fields: all available fields from the PHP model

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Divider as _Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  OpenInNew as WebIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import Autocomplete from '@mui/material/Autocomplete';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';
import {
  CatalogueCountrySwitch,
  catalogueCountryName,
  CatalogueUkAddressFields,
  UK_COUNTRY_NAME,
  useCatalogueCountry,
  toCatalogueCountry,
  type CatalogueCountry,
} from '../components/CatalogueCountrySwitch';
import { useCountries } from '../lib/countries';

// ---------------------------------------------------------------------------
// Lookup types for State / Suburb dropdowns
// ---------------------------------------------------------------------------

interface StateOption  { readonly state_id: number; readonly state: string; }
interface SuburbOption { readonly id: number; readonly suburb: string; readonly postcode: string; readonly country: string; }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format an ISO/UTC timestamp as Australian date-time: DD/MM/YYYY HH:MM:SS */
function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day:    '2-digit',
    month:  '2-digit',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d);
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FertilizerCompanyRow {
  readonly id: number;
  readonly company: string | null;
  readonly street: string | null;
  readonly suburb: string | null;
  readonly suburb_name: string | null;
  readonly state_name: string | null;
  readonly postcode: string | null;
  readonly state: string | null;
  readonly country: string | null;
  readonly phone_no: string | null;
  readonly mobile_number: string | null;
  readonly abn: string | null;
  readonly division: string | null;
  readonly title: string | null;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly email_address: string | null;
  readonly web_link: string | null;
  readonly status: number | null;
  readonly registration_date: string | null;
  readonly client_id: number | null;
  readonly invoice_prefix: string | null;
  readonly country_code?: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly created_by: string | null;
  readonly updated_by: string | null;
}

interface FormState {
  company: string;
  street: string;
  suburb: string;
  postcode: string;
  state: string;
  country: string;
  phone_no: string;
  mobile_number: string;
  abn: string;
  division: string;
  title: string;
  first_name: string;
  last_name: string;
  email_address: string;
  web_link: string;
  client_id: string;
  invoice_prefix: string;
  country_code: CatalogueCountry;
}

const DEFAULT_FORM: FormState = {
  company: '',
  street: '',
  suburb: '',
  postcode: '',
  state: '',
  country: '',
  phone_no: '',
  mobile_number: '',
  abn: '',
  division: '',
  title: '',
  first_name: '',
  last_name: '',
  email_address: '',
  web_link: '',
  client_id: '',
  invoice_prefix: '',
  country_code: 'AU',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function FertilizerCompaniesPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const [country, setCountry] = useCatalogueCountry();
  const countries = useCountries();

  // ---- List state ----
  const [rows, setRows] = useState<readonly FertilizerCompanyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [auditInfo, setAuditInfo] = useState<{ created_at: string | null; updated_at: string | null; created_by: string | null; updated_by: string | null } | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ---- State / Suburb cascading dropdowns ----
  const [stateOptions, setStateOptions]   = useState<readonly StateOption[]>([]);
  const [suburbOptions, setSuburbOptions] = useState<readonly SuburbOption[]>([]);
  const [loadingSuburbs, setLoadingSuburbs] = useState(false);
  const suburbDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/fertilizer-companies`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      url.searchParams.set('country', country);
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);

      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: FertilizerCompanyRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch, country]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // Load states when dialog opens
  useEffect(() => {
    if (!dialogOpen || !token) return;
    void (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/v1/admin/postcodes/states`, {
          headers: authHeaders(token),
        });
        if (res.ok) {
          const json = (await res.json()) as { data: StateOption[] };
          setStateOptions(json.data);
        }
      } catch { /* silent */ }
    })();
  }, [dialogOpen, token]);

  // Search suburbs server-side as the user types (debounced 300ms, min 2 chars)
  const handleSuburbInputChange = useCallback((_e: React.SyntheticEvent, inputValue: string) => {
    if (suburbDebounceRef.current) clearTimeout(suburbDebounceRef.current);
    if (!form.state || inputValue.length < 2) {
      setSuburbOptions([]);
      return;
    }
    setLoadingSuburbs(true);
    suburbDebounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(
            `${getApiUrl()}/api/v1/admin/postcodes/suburbs-by-state?state_id=${form.state}&search=${encodeURIComponent(inputValue)}`,
            { headers: authHeaders(token) },
          );
          if (res.ok) {
            const json = (await res.json()) as { data: SuburbOption[] };
            setSuburbOptions(json.data);
          }
        } catch { /* silent */ }
        finally { setLoadingSuburbs(false); }
      })();
    }, 300);
  }, [form.state, token]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm({ ...DEFAULT_FORM, country_code: country });
    setSuburbOptions([]);
    setFormError(null);
    setAuditInfo(null);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/fertilizer-companies/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: FertilizerCompanyRow };
      const r = json.data;
      setForm({
        company:           r.company ?? '',
        street:            r.street ?? '',
        suburb:            r.suburb ?? '',
        postcode:          r.postcode ?? '',
        state:             r.state ?? '',
        country:           r.country ?? '',
        phone_no:          r.phone_no ?? '',
        mobile_number:     r.mobile_number ?? '',
        abn:               r.abn ?? '',
        division:          r.division ?? '',
        title:             r.title ?? '',
        first_name:        r.first_name ?? '',
        last_name:         r.last_name ?? '',
        email_address:     r.email_address ?? '',
        web_link:          r.web_link ?? '',
        client_id:         r.client_id != null ? String(r.client_id) : '',
        invoice_prefix:    r.invoice_prefix ?? '',
        country_code:      toCatalogueCountry(r.country_code ?? country),
      });
      // Pre-populate suburb option so the Autocomplete shows the current value in edit mode
      if (r.suburb && r.suburb_name) {
        setSuburbOptions([{ id: Number(r.suburb), suburb: r.suburb_name, postcode: r.postcode ?? '', country: r.country ?? '' }]);
      } else {
        setSuburbOptions([]);
      }
      setAuditInfo({
        created_at:  r.created_at ?? null,
        updated_at:  r.updated_at ?? null,
        created_by:  r.created_by ?? null,
        updated_by:  r.updated_by ?? null,
      });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const trimmedCompany = form.company.trim();
    if (!trimmedCompany) { setFormError('Company name is required.'); return; }
    if (trimmedCompany.length > 300) { setFormError('Company must be 300 characters or fewer.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/fertilizer-companies/${editingId}`
        : `${getApiUrl()}/api/v1/admin/fertilizer-companies`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          company:           trimmedCompany,
          client_id:         form.client_id !== '' ? parseInt(form.client_id, 10) : null,
          invoice_prefix:    form.invoice_prefix || null,
          street:            form.street || null,
          suburb:            form.suburb || null,
          postcode:          form.postcode || null,
          state:             form.state || null,
          country:           form.country_code === 'GB' ? UK_COUNTRY_NAME : (form.country || null),
          phone_no:          form.phone_no || null,
          mobile_number:     form.mobile_number || null,
          abn:               form.abn || null,
          division:          form.division || null,
          title:             form.title || null,
          first_name:        form.first_name || null,
          last_name:         form.last_name || null,
          email_address:     form.email_address || null,
          web_link:          form.web_link || null,
          country_code:      form.country_code,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Record updated.' : 'Record added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/fertilizer-companies/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Record deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Fertilizer Companies</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage fertilizer and chemical manufacturers / suppliers
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Company
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <CatalogueCountrySwitch value={country} onChange={(c) => { setCountry(c); setPage(0); }} />
            <TextField
              size="small"
              placeholder="Search company, suburb, state…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 320 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Company</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 280 }}>Street</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 140 }}>Suburb</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 90 }}>Postcode</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 90 }}>State</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 165 }}>Phone</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 80 }}>Web Link</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell sx={{ fontWeight: 500 }}>{row.company ?? '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.street ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.suburb_name ?? row.suburb ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.postcode ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.state_name ?? row.state ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.phone_no ?? '—'}
                      </TableCell>
                      <TableCell align="center">
                        {row.web_link ? (
                          <Tooltip title={row.web_link}>
                            <IconButton
                              size="small"
                              component="a"
                              href={row.web_link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <WebIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => confirmDelete(row.id, row.company ?? String(row.id))}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
            >
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Fertilizer Company' : 'Add Fertilizer Company'}
          {form.country_code !== 'AU' && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 600 }}>
              {catalogueCountryName(countries, form.country_code)} catalogue
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Company + Division */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Company Name"
                size="small"
                sx={{ flex: 2 }}
                required
                inputProps={{ maxLength: 300 }}
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                autoFocus
              />
              <TextField
                label="Division"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 100 }}
                value={form.division}
                onChange={(e) => setForm((f) => ({ ...f, division: e.target.value }))}
              />
            </Box>
            {/* Row 2: Title + First Name + Last Name */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Title"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 50 }}
                placeholder="Mr / Ms / Dr"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
              <TextField
                label="First Name"
                size="small"
                sx={{ flex: 2 }}
                inputProps={{ maxLength: 211 }}
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              />
              <TextField
                label="Last Name"
                size="small"
                sx={{ flex: 2 }}
                inputProps={{ maxLength: 211 }}
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
              />
            </Box>
            {/* Row 3: Street */}
            <TextField
              label="Street"
              size="small"
              fullWidth
              inputProps={{ maxLength: 200 }}
              value={form.street}
              onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
            />
            {/* Row 4 (UK): Postcode → Town / County. Town text in suburb, county text in state. */}
            {form.country_code === 'GB' ? (
              <CatalogueUkAddressFields
                apiUrl={getApiUrl()}
                token={token}
                value={{ suburb: form.suburb, state: form.state, postcode: form.postcode }}
                onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              />
            ) : (
            /* Row 4: State → Suburb (cascading) → Postcode (auto-fill) + Country */
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ flex: 1.5 }}>
                <InputLabel>State</InputLabel>
                <Select
                  label="State"
                  value={form.state}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, state: e.target.value, suburb: '', postcode: '' }));
                    setSuburbOptions([]);
                  }}
                >
                  <MenuItem value=""><em>— Select State —</em></MenuItem>
                  {stateOptions.map((s) => (
                    <MenuItem key={s.state_id} value={String(s.state_id)}>{s.state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                sx={{ flex: 3 }}
                size="small"
                options={suburbOptions}
                loading={loadingSuburbs}
                disabled={!form.state}
                filterOptions={(x) => x}
                getOptionLabel={(o) => o.suburb}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={suburbOptions.find((s) => String(s.id) === form.suburb) ?? null}
                onInputChange={handleSuburbInputChange}
                noOptionsText={!form.state ? 'Select State first' : 'Type at least 2 characters…'}
                onChange={(_e, chosen) => {
                  setSuburbOptions([]);
                  setForm((f) => ({
                    ...f,
                    suburb:   chosen ? String(chosen.id) : '',
                    postcode: chosen?.postcode ?? f.postcode,
                    country:  chosen?.country  ?? f.country,
                  }));
                }}
                renderInput={(params) => (
                  // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
                  <TextField
                    {...params}
                    size="small"
                    label="Suburb"
                    placeholder={
                      !form.state ? 'Select State first' :
                      loadingSuburbs ? 'Searching…' : 'Type to search…'
                    }
                  />
                )}
              />
              <TextField
                label="Postcode"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 20, readOnly: !!form.suburb }}
                value={form.postcode}
                onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value }))}
              />
              <TextField
                label="Country"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 100 }}
                value={form.country}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              />
            </Box>
            )}
            {/* Row 5: Phone + Mobile + Email */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Phone"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 50 }}
                value={form.phone_no}
                onChange={(e) => setForm((f) => ({ ...f, phone_no: e.target.value }))}
              />
              <TextField
                label="Mobile"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 20 }}
                value={form.mobile_number}
                onChange={(e) => setForm((f) => ({ ...f, mobile_number: e.target.value }))}
              />
              <TextField
                label="Email"
                size="small"
                sx={{ flex: 2 }}
                type="email"
                inputProps={{ maxLength: 211 }}
                value={form.email_address}
                onChange={(e) => setForm((f) => ({ ...f, email_address: e.target.value }))}
              />
            </Box>
            {/* Row 6: ABN */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="ABN"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 100 }}
                value={form.abn}
                onChange={(e) => setForm((f) => ({ ...f, abn: e.target.value }))}
              />
            </Box>
            {/* Row 7: Website */}
            <TextField
              label="Website"
              size="small"
              fullWidth
              placeholder="https://…"
              value={form.web_link}
              onChange={(e) => setForm((f) => ({ ...f, web_link: e.target.value }))}
              InputProps={{
                endAdornment: form.web_link ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      color="success"
                      title="Open link"
                      onClick={() => { window.open(form.web_link, '_blank')?.focus(); }}
                    >
                      <WebIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {/* Audit Trail — edit mode only */}
            {editingId !== null && auditInfo !== null && (
              <Box
                mt={1}
                p={2}
                sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                  Audit Trail
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Add Company'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Fertilizer Company</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteLabel}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
